export interface TaxValues {
    [key: number]: {
        tax: number;
        price: number;
    };
}

export interface FinancialCalculationResult {
    taxes: TaxValues;
    positionTotal: number;
    netTotal: number;
    grossTotal: number;
}

export class FinancialCalculations {
    public static getNetTotal = (product: { quantity: number; price?: number; discount?: number }): number => {
        const total = product.quantity * (product.price || 0);
        const totalDiscount = (total * (product.discount || 0)) / 100;

        return Math.round((total - totalDiscount) * 100) / 100;
    };

    public static getTax = (product: { quantity: number; price?: number; discount?: number; tax?: number }): number => {
        const total = this.getNetTotal(product);
        return (total * (product.tax || 0)) / 100;
    };

    public static calculate = (
        items: { quantity: number; price?: number; discount?: number; tax?: number }[],
        netShippingCosts = 0,
        netSurcharge = 0,
        isCredit = false
    ): FinancialCalculationResult => {
        const taxes: TaxValues = {};
        let taxTotal = 0;

        let maxTaxRate = 0;

        // First, calculate item taxes (excluding shipping/surcharge)
        for (const item of items) {
            if (item.tax !== undefined && item.tax !== null) {
                if (item.tax > maxTaxRate) {
                    maxTaxRate = item.tax;
                }

                const tax = taxes[item.tax]?.tax || 0;
                const price = taxes[item.tax]?.price || 0;
                const itemTax = this.getTax(item);

                if (!taxes[item.tax]) {
                    taxes[item.tax] = { tax: 0, price: 0 };
                }

                taxes[item.tax].tax = tax + itemTax;
                taxes[item.tax].price = price + (this.getNetTotal(item) || 0);
                taxTotal += itemTax;
            }
        }

        // Apply shipping & surcharge using ONLY the highest tax rate
        let shippingTax = 0;
        let surchargeTax = 0;

        if (maxTaxRate > 0) {
            shippingTax = (netShippingCosts * maxTaxRate) / 100;
            surchargeTax = (netSurcharge * maxTaxRate) / 100;
            taxTotal += shippingTax + surchargeTax;

            if (!taxes[maxTaxRate]) {
                taxes[maxTaxRate] = { tax: 0, price: 0 };
            }

            taxes[maxTaxRate].tax += shippingTax + surchargeTax;
            taxes[maxTaxRate].price += netShippingCosts + netSurcharge;
        }

        // Calculate Totals
        taxTotal = Math.round(taxTotal * 100) / 100;

        const positionTotal = items.reduce((result, position) => {
            return (result += this.getNetTotal(position));
        }, 0);

        let netTotal = positionTotal + netShippingCosts + netSurcharge;
        let grossTotal = netTotal + taxTotal;

        netTotal = Math.round(netTotal * 100) / 100;
        grossTotal = Math.round(grossTotal * 100) / 100;

        if (isCredit) {
            netTotal = -Math.abs(netTotal);
            grossTotal = -Math.abs(grossTotal);
        }

        return {
            taxes,
            positionTotal,
            netTotal,
            grossTotal,
        };
    };
}
